import { cn } from '@/common/utils/utils'
import { useFetchHoldingsHistory, useListHoldings } from '@/trading/tradingQueries'
import moment from 'moment'
import { buildTimeSeries } from './portfolioHelpers'
import { useEffect, useMemo, useRef, useState } from 'react'
import { PortfolioValueGraph } from '@/common/ui/PortfolioValueGraph'
import { analytics } from '@/common/analytics/analytics'
import useAccountStore from '@/account/accountStore'

const RANGE_CONFIG = {
    '1W': { days: 7, label: '1W' },
    '1M': { days: 30, label: '1M' },
    '6M': { days: 180, label: '6M' },
    '1Y': { days: 365, label: '1Y' },
} as const

type RangeKey = keyof typeof RANGE_CONFIG

function GraphLabels({
    labels,
    onSelect,
    selectedLabel,
}: {
    labels: RangeKey[]
    onSelect: (label: RangeKey) => void
    selectedLabel: RangeKey
}) {
    return (
        <div className="w-full">
            <div className="w-full grid grid-cols-4 gap-2 md:flex md:flex-row md:justify-end md:gap-2">
                {labels.map((label) => (
                    <div
                        key={label}
                        className={cn('px-3 py-1.5 rounded-full justify-start items-center gap-6 flex', {
                            'bg-[#e3fbc5]': label === selectedLabel,
                        })}
                    >
                        <button
                            onClick={() => onSelect(label)}
                            aria-pressed={label === selectedLabel}
                            className="w-full text-center text-[#223a32] text-sm md:w-auto"
                        >
                            {label}
                        </button>
                    </div>
                ))}
            </div>
        </div>
    )
}

function LoadingView() {
    return (
        <div className="w-full min-h-[300px] animate-pulse flex space-x-4">
            <div className="rounded-full bg-stone-100 h-10 w-10"></div>
            <div className="flex-1 space-y-6 py-1">
                <div className="h-2 bg-stone-100 rounded"></div>
                <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-4">
                        <div className="h-2 bg-stone-100 rounded col-span-2"></div>
                        <div className="h-2 bg-stone-100 rounded col-span-1"></div>
                    </div>
                    <div className="h-2 bg-stone-100 rounded"></div>
                </div>
            </div>
        </div>
    )
}

export function PortfolioValue() {
    const activeAccountId = useAccountStore((s) => s.activeAccountId)
    const [selectedRange, setSelectedRange] = useState<RangeKey>('1M')
    const everHadTradesRef = useRef(false)
    const [graphValues, setGraphValues] = useState<{ x: number; y: number; percent?: number | null }[]>([])

    const rangePresets = Object.keys(RANGE_CONFIG) as RangeKey[]

    const { data: holdingsHistory, isLoading } = useFetchHoldingsHistory(activeAccountId, selectedRange)
    const { data: holdingsData } = useListHoldings(activeAccountId)

    const mappedSeries = useMemo(() => {
        if (isLoading || !holdingsHistory || holdingsHistory.length === 0)
            return [] as { x: number; y: number; percent?: number | null }[]

        const placeholderValue =
            holdingsHistory[holdingsHistory.length - 1].totalValue ||
            (holdingsData && holdingsData.holdings.length > 0
                ? holdingsData.holdings.reduce((acc: number, cur: any) => acc + cur.currentValue, 0)
                : 0)

        // Build deterministic start/end range based on preset days
        const days = RANGE_CONFIG[selectedRange].days
        const startDate = moment()
            .subtract(days - 1, 'days')
            .startOf('day')
        const endDate = moment().startOf('day')

        const holdingsAggregate =
            holdingsData && holdingsData.holdings.length > 0
                ? holdingsData.holdings.reduce((acc: number, cur: any) => acc + cur.currentValue, 0)
                : null

        return buildTimeSeries(holdingsHistory, startDate, endDate, holdingsAggregate, placeholderValue)
    }, [isLoading, holdingsHistory, holdingsData, selectedRange])

    const currentHasTraded = useMemo(() => {
        const seriesHas = (mappedSeries || []).some((m) => Number.isFinite(m.y) && m.y > 0)
        const holdingsHave = Boolean(
            holdingsData &&
                holdingsData.holdings &&
                holdingsData.holdings.some((h: any) => Number.isFinite(h.currentValue) && h.currentValue > 0),
        )
        return seriesHas || holdingsHave
    }, [mappedSeries, holdingsData])

    useEffect(() => {
        if (!mappedSeries || mappedSeries.length === 0) {
            setGraphValues([])
            return
        }

        setGraphValues(mappedSeries)
    }, [mappedSeries])

    // Reset per-account observed state when switching accounts so
    // an account with no trades doesn't show the previous account's graph.
    useEffect(() => {
        everHadTradesRef.current = false
    }, [activeAccountId])

    // Mark that we've observed real trade data; once true, never revert
    useEffect(() => {
        if (currentHasTraded) everHadTradesRef.current = true
    }, [currentHasTraded])

    return isLoading && graphValues.length === 0 ? (
        <div className="w-full flex-col items-start inline-flex">
            <LoadingView />
            <GraphLabels
                labels={rangePresets}
                onSelect={(label: RangeKey) => {
                    analytics.trackWithDefaults('PortfolioGraph PresetRangeClicked', {
                        range: label,
                    })
                    setSelectedRange(label)
                }}
                selectedLabel={selectedRange}
            />
        </div>
    ) : everHadTradesRef.current || currentHasTraded ? (
        <div className="w-full flex-col items-start inline-flex">
            <PortfolioValueGraph
                data={graphValues}
                isFake={false}
                isFetchingMore={isLoading && graphValues.length > 0}
            />
            <GraphLabels
                labels={rangePresets}
                onSelect={(label: RangeKey) => {
                    analytics.trackWithDefaults('PortfolioGraph PresetRangeClicked', {
                        range: label,
                    })
                    setSelectedRange(label)
                }}
                selectedLabel={selectedRange}
            />
        </div>
    ) : null
}
